import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import React, { useCallback } from 'react';

import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowRight, Icon } from 'chatie-design-system/icon';

import EFinderImage from '@components/common/EFinderImage';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IEFinderObject } from '@model';
import { MentionType, getMentionPath } from '@utils/mention';
import { formatKoreanCompactNumber } from '@utils/number';
import { useWebView } from '@utils/webview';

import Avatar, { AvatarSize } from '../common/Avatar';

const cardButtonCSS = (theme: Theme) => css`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${Spacing.small}px;
  padding: ${Spacing.small}px;
  border-radius: ${Radius.medium}px;
  border: 1px solid ${theme.var.gray100};
  background: linear-gradient(180deg, #ffffff 0%, ${theme.var.gray50} 100%);
  text-align: left;

  &:active {
    background: ${theme.var.gray50};
  }

  .thumb {
    position: relative;
    width: 56px;
    height: 56px;
    overflow: hidden;
    flex-shrink: 0;
    border-radius: ${Radius.small}px;
    background: ${theme.var.gray100};
  }

  .meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
    gap: 2px;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 2px 6px;
    border-radius: 999px;
    background: ${theme.colors.main50};
    color: ${theme.colors.main500};
    ${Typography.body11};
    font-weight: 600;
  }

  .title {
    ${Typography.body14};
    font-weight: 700;
    color: ${theme.var.gray900};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .description {
    ${Typography.body12};
    color: ${theme.var.gray600};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stats {
    ${Typography.body11};
    color: ${theme.var.gray500};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chevron {
    flex-shrink: 0;
  }
`;

const userThumbWrapCSS = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const fallbackThumbCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.var.gray100};
  color: ${theme.var.gray400};
  ${Typography.body12};
`;

export type MentionCardModel =
  | {
      type: 'u';
      id: number;
      title: string;
      description?: string | null;
      image?: IEFinderObject<'image'>;
      followerCount: number;
      storyCount: number;
    }
  | {
      type: 's';
      id: number;
      title: string;
      description?: string | null;
      image?: IEFinderObject<'image'>;
      authorNickname?: string | null;
      tapCount: number;
    }
  | {
      type: 'c';
      id: number;
      title: string;
      description?: string | null;
      image?: IEFinderObject<'image'>;
      messageCount: number;
      likeCount: number;
    };

type MentionCardPresentation = {
  title: string;
  description: string;
  stats: string;
  thumb: React.ReactNode;
};

export const createUserMentionCardModel = ({
  id,
  nickname,
  introduce,
  picture,
  followerCount,
  storyCount,
}: {
  id: number;
  nickname: string;
  introduce?: string | null;
  picture?: IEFinderObject<'image'>;
  followerCount: number;
  storyCount: number;
}): MentionCardModel => ({
  type: 'u',
  id,
  title: nickname,
  description: introduce,
  image: picture,
  followerCount,
  storyCount,
});

export const createStoryMentionCardModel = ({
  id,
  title,
  summary,
  coverImage,
  authorNickname,
  tapCount,
}: {
  id: number;
  title: string;
  summary?: string | null;
  coverImage?: IEFinderObject<'image'>;
  authorNickname?: string | null;
  tapCount: number;
}): MentionCardModel => ({
  type: 's',
  id,
  title,
  description: summary,
  image: coverImage,
  authorNickname,
  tapCount,
});

export const createCharacterMentionCardModel = ({
  id,
  name,
  summary,
  image,
  messageCount,
  likeCount,
}: {
  id: number;
  name: string;
  summary?: string | null;
  image?: IEFinderObject<'image'>;
  messageCount: number;
  likeCount: number;
}): MentionCardModel => ({
  type: 'c',
  id,
  title: name,
  description: summary,
  image,
  messageCount,
  likeCount,
});

const getMentionEyebrow = (type: MentionType) => {
  switch (type) {
    case 'u':
      return '유저';
    case 's':
      return '작품';
    case 'c':
      return '캐릭터';
  }
};

const MentionCardButton = ({
  model,
  card,
}: {
  model: MentionCardModel;
  card: MentionCardPresentation;
}) => {
  const router = useRouter();
  const theme = useTheme();
  const { platform } = useGlobalContext();
  const { web } = useWebView();

  const navigate = useCallback(
    (path: string) => {
      if (platform === Platform.web) {
        router.push(path);
      } else {
        web.openPage(path);
      }
    },
    [platform, router, web]
  );

  return (
    <button
      type="button"
      css={cardButtonCSS(theme)}
      onClick={(event) => {
        event.stopPropagation();
        navigate(getMentionPath(model));
      }}
    >
      <div className="thumb">{card.thumb}</div>
      <div className="meta">
        <span className="eyebrow">{getMentionEyebrow(model.type)}</span>
        <strong className="title">{card.title}</strong>
        <span className="description">{card.description}</span>
        <span className="stats">{card.stats}</span>
      </div>
      <span className="chevron">
        <Icon icon={ArrowRight} size={Sizes.small} color={theme.var.gray400} />
      </span>
    </button>
  );
};

const MentionCard = ({ model }: { model: MentionCardModel }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  let card: MentionCardPresentation;

  switch (model.type) {
    case 'u':
      card = {
        title: model.title,
        description: model.description || t('유저 프로필 보기'),
        stats: `${t('팔로워')} ${t('formatNumber', {
          val: model.followerCount || 0,
        })} · ${t('작품')} ${t('formatNumber', {
          val: model.storyCount || 0,
        })}`,
        thumb: (
          <div css={userThumbWrapCSS}>
            <Avatar
              id={model.id}
              picture={model.image}
              size={AvatarSize.large}
            />
          </div>
        ),
      };
      break;
    case 's': {
      const tapStats = `${t('탭')} ${t('formatNumber', {
        val: model.tapCount || 0,
      })}`;
      card = {
        title: model.title,
        description: model.description || model.authorNickname || t('작품'),
        stats: model.authorNickname
          ? `${model.authorNickname} · ${tapStats}`
          : tapStats,
        thumb: model.image ? (
          <EFinderImage image={model.image} alt={model.title} />
        ) : (
          <div css={fallbackThumbCSS(theme)}>{t('작품')}</div>
        ),
      };
      break;
    }
    case 'c':
      card = {
        title: model.title,
        description: model.description || t('캐릭터 프로필 보기'),
        stats: `${t('플레이')} ${formatKoreanCompactNumber(
          model.messageCount || 0
        )} · ${t('좋아요')} ${t('formatNumber', {
          val: model.likeCount || 0,
        })}`,
        thumb: model.image ? (
          <EFinderImage image={model.image} alt={model.title} />
        ) : (
          <div css={fallbackThumbCSS(theme)}>{t('캐릭터')}</div>
        ),
      };
      break;
  }

  return <MentionCardButton model={model} card={card} />;
};

export default MentionCard;
